import React, {createRef, ReactNode, RefObject} from "react";
import FunctionHelper from "../helper/FunctionHelper";
import Editor, {Monaco} from "@monaco-editor/react";
import Translate from "../translation/Translate";
import {Button} from "primereact/button";
import FileInput from "./FileInput";
import DomHelper from "../helper/DomHelper";
import {Base64} from "js-base64";
import {Menu} from "primereact/menu";
import Immutable from "../immutable/Immutable";
import {MenuItem} from "primereact/menuitem";
import PortalPureComponent from "./PortalPureComponent";

/**
 * A variable to insert via menu
 */
export interface CodeInputVariable {
	label: string,
	value: string
}

/**
 * The state of a {@link CodeInput} component
 */
interface CodeInputState {
	clipboardReadable: boolean;
}

/**
 * The properties for a {@link CodeInput} component
 */
export interface CodeInputProperties {
	inputId?: string;
	disabled?: boolean;
	className?: string;
	style?: React.CSSProperties;
	value?: string;
	language?: string;
	options?: any;
	height?: string;
	accept?: string;
	variables?: Immutable<Array<CodeInputVariable>>;
	tooltip?: string;
	buttons?: ReactNode;
	hideButtons?: boolean;

	onChange?(e: {
		value: string;
	}): void;
}

/**
 * This component wraps the {@link Editor} element
 */
export class CodeInputDefinition extends PortalPureComponent<CodeInputProperties, CodeInputState> {
	private codeFile: RefObject<FileInput>;
	private editor: Monaco;
	private menuId: string;
	private menu: RefObject<Menu>;

	/**
	 * Constructs a {@link ListInput} component
	 */
	constructor(props: Readonly<CodeInputProperties>) {
		super(props);

		this.state = {
			clipboardReadable: false
		};

		this.codeFile = createRef<FileInput>();
		this.menu = createRef<Menu>();
		this.menuId = DomHelper.generateUUID();

		this.onChange = this.onChange.bind(this);
		this.onDrop = this.onDrop.bind(this);
		this.insertVariable = this.insertVariable.bind(this);
		this.reformatCode = this.reformatCode.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		try {
			let status: PermissionStatus | undefined = await navigator.permissions.query({
				name: "clipboard-read" as PermissionName
			});

			this.setState({
				clipboardReadable: status?.state === "granted" || status?.state === "prompt"
			});
		} catch (e: any) {
			// can be ignored permission is denied
		}
	}

	/**
	 * Called when a file is dropped on this element
	 */
	private async onDrop(event: React.DragEvent<HTMLDivElement>): Promise<void> {
		let files: FileList = event.dataTransfer.files;
		if (!FunctionHelper.exists(this.props.onChange) || files.length === 0) {
			return;
		}

		let content: string = await files[0].text();
		this.props.onChange!({value: content});
	}

	/**
	 * Called when the input value changes and calls the given onChange function from props
	 */
	private onChange(value: string | undefined): void {
		if (!FunctionHelper.exists(this.props.onChange)) {
			return;
		}

		this.props.onChange!({value: FunctionHelper.applyStringValue(value, "")});
	}

	private insertVariable(variable: string) {
		if (!FunctionHelper.exists(this.editor)) {
			return;
		}

		this.editor.trigger('keyboard', 'type', {text: variable});
	}

	private reformatCode() {
		if (!FunctionHelper.exists(this.editor)) {
			return;
		}

		this.editor.getAction('editor.action.formatDocument').run();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let className: Array<string> = ["input-code", "p-inputgroup", "tooltip"];
		className = className.concat(FunctionHelper.applyValue(this.props.className?.split(" "), []));
		if (this.props.disabled) {
			className.push("p-disabled");
		}

		let menuItems: Array<MenuItem> = [];
		let variables: Array<CodeInputVariable> = FunctionHelper.applyValue(this.props.variables?.get(), []);
		for (let variable of variables) {
			menuItems.push(
				{
					label: variable.label,
					command: (e) => {
						this.insertVariable(variable.value);
					}
				}
			);
		}

		let options = {
			"autoIndent": true,
			"formatOnPaste": true,
			"formatOnType": true,
			...this.props.options
		}

		return (
			<>
				<div onDrop={this.onDrop} className={className.join(" ")} data-pr-tooltip={this.props.tooltip}>
					<Editor value={FunctionHelper.applyStringValue(this.props.value, "")} onChange={this.onChange}
							language={this.props.language} options={options}
							height={FunctionHelper.applyStringValue(this.props.height, "200px")}
							onMount={(editor, monaco) => {
								this.editor = editor;
							}}/>
					<div className="p-inputgroup-addon vertical" hidden={this.props.hideButtons}>
						<Button icon="icon icon-close" tooltip={Translate.value("inputs.code.clear")}
								onClick={() => this.onChange("")}/>
						<Button icon="icon icon-upload small" tooltip={Translate.value("inputs.code.upload")}
								onClick={() => this.codeFile.current?.showFileDialog()}/>
						{this.state.clipboardReadable &&
							<Button icon="icon icon-clipboard small"
									tooltip={Translate.value("inputs.code.clipboard")}
									onClick={async () => this.onChange(await navigator.clipboard.readText())}/>
						}
						<Button icon="icon icon-reformat" tooltip={Translate.value("inputs.code.reformat")}
								onClick={() => this.reformatCode()}/>
						{menuItems.length > 0 &&
							<>
								<Button icon="icon icon-code small"
										tooltip={Translate.value("inputs.code.variables")}
										onClick={async (event) =>
											this.menu.current!.toggle(event)
										}/>
								<Menu popup={true} ref={this.menu} id={this.menuId}
									  appendTo={document.getElementById("root")}
									  model={menuItems}/>
							</>
						}
						{this.props.buttons}
					</div>
				</div>
				<div style={{display: "none"}}>
					<FileInput inputId="control-code-file" ref={this.codeFile} accept={this.props.accept}
							   onChange={(files) => {
								   if (files.length === 0 || !FunctionHelper.exists(files[0].value)) {
									   return;
								   }

								   this.onChange(Base64.atob(files[0].value));
							   }}/>
				</div>
			</>
		);
	}
}

/**
 * A CodeInput component is used to input formatted and structured code as a textarea
 */
const
	CodeInput = React.memo(CodeInputDefinition,
		(prevProps: Readonly<CodeInputProperties>, nextProps: Readonly<CodeInputProperties>) => {
			return ![
				FunctionHelper.exists(nextProps.disabled) && nextProps.disabled !== prevProps.disabled,
				FunctionHelper.exists(nextProps.value) && nextProps.value !== prevProps.value,
				FunctionHelper.exists(nextProps.className) && nextProps.className !== prevProps.className,
				FunctionHelper.exists(nextProps.style) && nextProps.style !== prevProps.style,
				FunctionHelper.exists(nextProps.language) && nextProps.language !== prevProps.language,
				FunctionHelper.exists(nextProps.options) && nextProps.options !== prevProps.options,
				FunctionHelper.exists(nextProps.accept) && nextProps.accept !== prevProps.accept,
				FunctionHelper.exists(nextProps.tooltip) && nextProps.tooltip !== prevProps.tooltip,
				FunctionHelper.exists(nextProps.buttons) && nextProps.buttons !== prevProps.buttons,
				FunctionHelper.exists(nextProps.hideButtons) && nextProps.hideButtons !== prevProps.hideButtons,
				FunctionHelper.exists(nextProps.variables) && !nextProps.variables!.equals(prevProps.variables),
			].includes(true);
		}
	);

export default CodeInput;